import { useCallback, useEffect, useState } from "react";
import {
  AUTH_SESSION_UPDATED,
  ensureFreshAccessToken,
  isAccessExpiredOrSoon,
} from "@/services/tokenRefresh";
import { isGluceraPublicAuthRoute } from "@/lib/authBootstrapGate";
import { getGluceraAccessToken } from "../utils/storage";
import { useGluceraAuthContext } from "../context/GluceraAuthContext";

const SESSION_BUFFER_MS = 5 * 60 * 1000;

/**
 * Ensures token refresh completes before authenticated API calls fire.
 * Does not refresh or enable APIs until GluceraAuth reports the user is signed in.
 */
export function useEnsureSessionReady() {
  const { isAuthenticated, loading: authLoading } = useGluceraAuthContext();
  const [sessionReady, setSessionReady] = useState(false);

  const prepareSession = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setSessionReady(true);
      return;
    }

    await ensureFreshAccessToken(SESSION_BUFFER_MS);
    setSessionReady(true);
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    setSessionReady(false);
    void prepareSession();
  }, [prepareSession]);

  useEffect(() => {
    const onSessionUpdated = () => {
      if (isAuthenticated) {
        void prepareSession();
      }
    };
    window.addEventListener(AUTH_SESSION_UPDATED, onSessionUpdated);
    return () => {
      window.removeEventListener(AUTH_SESSION_UPDATED, onSessionUpdated);
    };
  }, [isAuthenticated, prepareSession]);

  const isLoggedIn = isAuthenticated;

  /** True only after auth is resolved and a valid access token exists. */
  const canUseAuthenticatedApis =
    !authLoading &&
    !isGluceraPublicAuthRoute() &&
    isAuthenticated &&
    sessionReady &&
    Boolean(getGluceraAccessToken()) &&
    !isAccessExpiredOrSoon(SESSION_BUFFER_MS);

  return { sessionReady, isLoggedIn, canUseAuthenticatedApis, prepareSession };
}

export { SESSION_BUFFER_MS };
